import cx from "classnames";
import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import React from "react";
import type { MSADecisionLineItemType } from "types/DataTypes";
import Tooltip from "components/Tooltip/Tooltip";

import {
  PrescriptionDecisionContent,
  TreatmentDecisionContent,
} from "./CMSDecisionModalForm";
import { useCMSDecisionModal } from "./useCMSDecisionModal";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  decisionItem: MSADecisionLineItemType | null;
  onDelete: () => void;
};

const CMSDecisionModal: React.FC<ModalProps> = ({
  decisionItem,
  isOpen,
  onClose,
  onDelete,
}: ModalProps) => {
  const {
    control,
    formMethods: {
      formState: { errors },
    },
    handleSave,
    isMedical,
    savingFields,
    proposedValues,
    isAnySaving,
  } = useCMSDecisionModal(
    isOpen,
    decisionItem || ({} as MSADecisionLineItemType),
  );

  // if no decision item, don't render the modal content
  if (!decisionItem) {
    return null;
  }

  const labelTypeSuffix = isMedical ? "Treatment" : "Rx";
  const isDeletable =
    decisionItem.proposed_prescription_allocation === null &&
    decisionItem.proposed_treatment_allocation === null;

  return (
    <div className={cx("overlay overlay-slidedown", { open: isOpen })}>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .input-wrapper.saving input {
            background-color: rgba(0, 123, 255, 0.1);
            border-color: #007bff;
            transition: all 0.3s ease;
          }
        `,
        }}
      />
      <div className="modal-content">
        <div className="grid-layout gl-1 modal-grid-wrapper">
          <div className="data-block no-hover">
            <div className="data-block-header flex ai-c jc-sb">
              CMS Decision Details ({labelTypeSuffix})
              {isAnySaving ? (
                <Tooltip
                  content="Please wait for changes to save before closing"
                  variant="warning"
                >
                  <ButtonIcon
                    kind="neutral"
                    onClick={onClose}
                    disabled={isAnySaving}
                  >
                    close
                  </ButtonIcon>
                </Tooltip>
              ) : (
                <ButtonIcon kind="neutral" onClick={onClose}>
                  close
                </ButtonIcon>
              )}
            </div>

            {isMedical ? (
              <TreatmentDecisionContent
                name={proposedValues.name}
                control={control}
                errors={errors}
                itemId={Number(decisionItem.id)}
                handleSave={handleSave}
                savingFields={savingFields}
              />
            ) : (
              <PrescriptionDecisionContent
                name={proposedValues.name}
                control={control}
                errors={errors}
                itemId={Number(decisionItem.id)}
                handleSave={handleSave}
                savingFields={savingFields}
              />
            )}

            <div className="flex jc-c ai-c pd-top pd-bottom">
              {isAnySaving ? (
                <Tooltip
                  content="Please wait for changes to save before closing"
                  variant="warning"
                >
                  <Button onClick={onClose} disabled={isAnySaving}>
                    Close
                  </Button>
                </Tooltip>
              ) : (
                <Button onClick={onClose}>Close</Button>
              )}
              {isDeletable && <Button onClick={onDelete}>Delete</Button>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CMSDecisionModal;
